import { ArrowLeft, ChevronDown, Phone, PhoneForwarded, PhoneOff, UserPen, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import AddPerson from '../dialogs/AddPerson';
import { redirectToContact } from '../shared/customUiSdk';
import Dialogs from '../shared/Dialogs';
import Translate from '../shared/Translate';
import { formatDateTime, formatDurationFromDates, formatDurationFromSeconds, formatPhoneNumber, getTextColorClassName, errorLogEnabled } from '../shared/Utils';
import WsContainers from '../shared/WsContainers';
import '../styles/call.css';

const CallDetail = (props) => {
  const displayCallParty = (number, name) => {
    const personInfo = props.fullScreenNotification.pipedriveData?.persons?.find(person => person.phoneNumber === number);
    return (
      <div className="commlog-party-info">
        <div className="commlog-party-name">
          {
            personInfo
              ? <span onClick={() => redirectToContact(props.sdk, personInfo.personId)}>
                {personInfo.personName}
              </span>
              : (name
                ? <>
                  <div className='commlog-party-title'>{name}</div>
                  <div className="source-label">{Translate.get('common.fromOptimCall.label')}</div>
                  <button
                    className="add-button"
                    onClick={() => Dialogs.open(
                      <AddPerson credentials={props.credentials} sdk={props.sdk} phoneNumber={number} providedName={name} />
                    )}
                  >
                    {Translate.get('common.add.button')}
                  </button>
                </>
                : <>
                  <div className='commlog-party-title'>{Translate.get('common.unknown.label')}</div>
                  <button
                    className="add-button"
                    onClick={() => Dialogs.open(
                      <AddPerson credentials={props.credentials} sdk={props.sdk} phoneNumber={number} />
                    )}
                  >
                    {Translate.get('common.add.button')}
                  </button>
                </>
              )
          }
        </div>
        <div className="commlog-party-number">{formatPhoneNumber(number)}</div>
      </div>
    )
  }

  const callType = (callType) => {
    switch (callType) {
      case 'inbound':
        return Translate.get('call.type.inbound');
      case 'outbound':
        return Translate.get('call.type.outbound');
      case 'internal':
        return Translate.get('call.type.internal');
      case 'external':
        return Translate.get('call.type.external');
      default:
        return Translate.get('call.type.unknown');
    }
  }

  const isAttendedTransfer = (props) => {
    return props.fullScreenNotification.pipedriveData?.callPhase === 'Conversation' &&
      (props.call.callLegs || []).filter(leg => leg.state === "ringing" || leg.state === "connected").length > 2;
  }

  const isPossibleToStartTransfer = (props) => {
    return props.fullScreenNotification.pipedriveData?.callPhase === 'Conversation' &&
      (props.call.callLegs || []).filter(leg => leg.state === "connected").length === 2 &&
      (props.call.callLegs || []).filter(leg => leg.state === "ringing").length === 0;
  }

  //const cookies = getCookies();
  const [transferNumber, setTransferNumber] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  // Function to update the current time
  const updateCurrentTime = () => {
    setCurrentTime(new Date().getTime());
  };

  // Set up an interval to update the current time every second
  useEffect(() => {
    const timer = setInterval(updateCurrentTime, 1000);

    // Clean up the interval when component unmounts
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="call-detail-container">
      <div className="call-detail-content">
        <div className="flex flex-col">
          <div className="header">
            <div className="header-left">
              <div className="back-icon-container">
                <ArrowLeft
                  size={24}
                  onClick={() => {
                    props.container.tell({
                      _class: 'com.optimsys.costra.optimcall.pipedrive.user.CurrentUserInfo$CloseFullScreenNotification',
                    })
                  }}
                />
              </div>
              <div className="call-info">
                <div className="call-title">{callType(props.call.callType)}</div>
                <div className="call-time">{formatDateTime(props.call.startTime, props.serverTimeOffset)}</div>
              </div>
            </div>
          </div>

          <div className="flex flex-row justify-between">
            <div className="call-detail-main">
              <div className="call-detail-parties">
                {displayCallParty(props.call.callerNumber, props.call.callerName)}
                <div className="dropdown-arrow"><ChevronDown size={24} /></div>
                {displayCallParty(props.call.calledNumber, props.call.calledName)}

                {(props.call.callType === 'inbound' && props.call.connectedNumber) && <>
                  <div className="dropdown-arrow"><ChevronDown size={24} /></div>
                  {displayCallParty(props.call.connectedNumber, props.call.connectedName)}
                </>}
              </div>
            </div>
            <div className="stats-container">
              <div className={'stats-state ' + getTextColorClassName(props.fullScreenNotification)}>
                {Translate.get('call.phase.' + (props.fullScreenNotification.pipedriveData?.callPhase || 'Unknown'))}
              </div>
              <div className="avatar">
                <div className={'avatar-icon ' + getTextColorClassName(props.fullScreenNotification)}>
                  <Users size={24} />
                </div>
              </div>
              <div className="stats-content">
                {props.fullScreenNotification.pipedriveData?.callPhase === 'Unknown' ? (
                  <div className="stat-item">
                    <div className="stat-label">
                      {Translate.get('call.detail.duration')}
                    </div>
                    <div className="stat-value">{formatDurationFromSeconds(props.call.totalLength)}</div>
                  </div>
                ) : (
                  <>
                    <div className="stat-item">
                      <div className="stat-label">
                        {Translate.get('call.detail.duration')}
                      </div>
                      <div className="stat-value">{
                        props.fullScreenNotification.pipedriveData?.callPhase === 'Disconnected'
                          ? formatDurationFromSeconds(props.call.totalLength)
                          : formatDurationFromDates(props.call.startTime, currentTime, props.serverTimeOffset)
                      }</div>
                    </div>
                    {(props.fullScreenNotification.pipedriveData?.callPhase !== 'Disconnected' ||
                      props.fullScreenNotification.pipedriveData?.conversationStartedAt
                    ) &&
                      <div className="stat-item">
                        <div className="stat-label">
                          {Translate.get('call.detail.conversationDuration')}
                        </div>
                        <div className={'stat-value' + (props.fullScreenNotification.pipedriveData?.conversationStartedAt ? ' with-text-color-call-conversation' : '')}>
                          {props.fullScreenNotification.pipedriveData?.conversationStartedAt
                            ? props.fullScreenNotification.pipedriveData?.callDisconnectedAt
                              ? formatDurationFromSeconds(props.call.speechLength)
                              : formatDurationFromDates(props.fullScreenNotification.pipedriveData.conversationStartedAt, currentTime, props.serverTimeOffset)
                            : "0:00"
                          }
                        </div>
                      </div>
                    }
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {props.fullScreenNotification.pipedriveData?.allowedCallControl && 
         props.fullScreenNotification.pipedriveData?.callPhase !== 'Disconnected' &&
          <button
            className="disconnect-button"
            onClick={() => {
              WsContainers.action({
                _class: 'com.optimsys.costra.optimcall.pipedrive.call.Disconnect',
                credentials: props.credentials,
                callId: props.call.callId,
              }).catch(error => {
                errorLogEnabled && console.error('Error on call disconnect command', error)
              })
            }}
          >
            <span className="disconnect-icon">
              <Phone size={24} rotate={135} />
            </span> {Translate.get('call.detail.disconnect.button')}
          </button>
        }

        {props.fullScreenNotification.pipedriveData?.allowedCallControl && isAttendedTransfer(props) &&
          <div className="transfer-section">
            <button
              className="cancel-transfer-button"
              onClick={() => {
                WsContainers.action({
                  _class: 'com.optimsys.costra.optimcall.pipedrive.call.CallAttendedTransferCancel',
                  credentials: props.credentials,
                  callId: props.call.callId,
                }).catch(error => {
                  errorLogEnabled && console.error('Error on call attended transfer cancel command', error)
                })
              }}
            >
              <span className="transfer-icon">
                <PhoneOff size={24} />
              </span> {Translate.get('call.detail.transfer.attended.cancel.button')}
            </button>
          </div>
        }

        {props.fullScreenNotification.pipedriveData?.allowedCallControl && isPossibleToStartTransfer(props) &&
          <div className="transfer-section">
            <div className="transfer-label">
              {Translate.get('call.detail.transfer')}
            </div>
            <div className="transfer-input-container">
              <input
                type="text"
                className="transfer-input"
                value={transferNumber}
                onChange={(e) => setTransferNumber(e.target.value)}
              />
              {props.call.callType === 'inbound' &&
                <button
                  className="transfer-button"
                  disabled={!transferNumber}
                  onClick={() => {
                    WsContainers.action({
                      _class: 'com.optimsys.costra.optimcall.pipedrive.call.CallBlindTransfer',
                      credentials: props.credentials,
                      callId: props.call.callId,
                      to: transferNumber,
                    }).catch(error => {
                      errorLogEnabled && console.error('Error on call blind transfer command', error)
                    })
                  }}
                >
                  <span className="transfer-icon">
                    <PhoneForwarded size={24} />
                  </span> {Translate.get('call.detail.transfer.blind.start.button')}
                </button>
              }
              <button
                className="transfer-button"
                disabled={!transferNumber}
                onClick={() => {
                  WsContainers.action({
                    _class: 'com.optimsys.costra.optimcall.pipedrive.call.CallAttendedTransferStart',
                    credentials: props.credentials,
                    callId: props.call.callId,
                    to: transferNumber,
                  }).catch(error => {
                    errorLogEnabled && console.error('Error on call attended transfer start command', error)
                  })
                }}
              >
                <span className="transfer-icon">
                  <UserPen size={24} />
                </span> {Translate.get('call.detail.transfer.attended.start.button')}
              </button>
            </div>
          </div>
        }
      </div>
    </div>
  );
};

export default CallDetail;